import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { MonitorIcon, ShieldIcon, SmartphoneIcon, TrashIcon } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { ToneBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminService, type AdminSession } from "@/gen/wabms/admin/v1/admin_pb";
import { adminKeys } from "@/lib/admin-queries";
import { errorMessage } from "@/lib/errors";
import { formatAbsolute, formatRelative, toDate } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";

export function SessionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAuthenticated = useUiStore((s) => s.setAuthenticated);

  const sessionsQuery = useQuery(AdminService.method.listSessions, {});
  const rows = sessionsQuery.data?.sessions ?? [];

  const [pending, setPending] = useState<AdminSession | undefined>(undefined);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);

  const revokeSession = useMutation(AdminService.method.revokeSession);
  const revokeAllSessions = useMutation(AdminService.method.revokeAllSessions);

  const others = rows.filter((s) => !s.current);

  function invalidateSessions(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: adminKeys.sessions() });
  }

  /** Revoking your own session ends this browser's access immediately. */
  function endOwnSession() {
    setAuthenticated(false);
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  async function revoke(session: AdminSession) {
    setPending(undefined);
    const wasCurrent = session.current;
    const p = revokeSession.mutateAsync({ sessionId: session.sessionId }).then(async (res) => {
      if (wasCurrent) endOwnSession();
      else await invalidateSessions();
      return res;
    });
    toast.promise(p, {
      loading: "Revoking session…",
      success: wasCurrent
        ? "Your session was revoked. Sign in again to continue."
        : "Session revoked. That browser is signed out immediately.",
      error: (err: unknown) => errorMessage(err),
    });
    await p.catch(() => undefined);
  }

  async function revokeAll() {
    setRevokeAllOpen(false);
    const p = revokeAllSessions.mutateAsync({ exceptCurrent: true }).then(async (res) => {
      await invalidateSessions();
      return res;
    });
    toast.promise(p, {
      loading: "Revoking sessions…",
      success: (res) =>
        `${res.revokedCount} session${res.revokedCount === 1 ? "" : "s"} revoked. This one is still signed in.`,
      error: (err: unknown) => errorMessage(err),
    });
    await p.catch(() => undefined);
  }

  return (
    <>
      <PageHeader
        title="Admin sessions"
        description="Every browser signed in with the system passphrase. Sessions are server-side records, so revoking one takes effect on the next request rather than at token expiry."
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={others.length === 0}
            onClick={() => setRevokeAllOpen(true)}
          >
            <TrashIcon /> Revoke all others
          </Button>
        }
      />

      {sessionsQuery.isError ? (
        <ErrorState
          error={sessionsQuery.error}
          title="Could not load sessions"
          onRetry={() => void sessionsQuery.refetch()}
        />
      ) : sessionsQuery.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : rows.length === 0 ? (
        <EmptyState icon={<ShieldIcon />} title="No active sessions" />
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Session</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Signed in</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((session) => {
                const expires = toDate(session.expiresAt);
                const expired = expires !== undefined && expires.getTime() < Date.now();
                return (
                  <TableRow key={session.sessionId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs">{session.sessionId}</code>
                        {session.current ? (
                          <ToneBadge tone="success">this browser</ToneBadge>
                        ) : null}
                        {expired ? <ToneBadge tone="warning">expired</ToneBadge> : null}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-72">
                      <div className="flex items-center gap-2">
                        {/Mobile|Android|iPhone/.test(session.userAgent) ? (
                          <SmartphoneIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        ) : (
                          <MonitorIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate text-xs text-muted-foreground">
                          {session.userAgent}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{session.ip}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatAbsolute(session.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(session.lastSeenAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(session.expiresAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Revoke session"
                        onClick={() => setPending(session)}
                      >
                        <TrashIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDialog
        open={pending !== undefined}
        onOpenChange={(open) => !open && setPending(undefined)}
        title={pending?.current ? "Revoke your own session?" : "Revoke this session?"}
        confirmLabel="Revoke session"
        destructive
        description={
          pending?.current ? (
            <p>This is the browser you are using. You will be signed out immediately.</p>
          ) : (
            <p>
              The browser at <code className="font-mono">{pending?.ip}</code> is signed out on its
              next request. It can sign in again with the system passphrase.
            </p>
          )
        }
        onConfirm={() => pending && void revoke(pending)}
      />

      <ConfirmDialog
        open={revokeAllOpen}
        onOpenChange={setRevokeAllOpen}
        title="Revoke all other sessions?"
        confirmLabel={`Revoke ${others.length} session${others.length === 1 ? "" : "s"}`}
        destructive
        description={
          <>
            <p>
              Signs out every other browser. This session stays active, so you will not lock
              yourself out.
            </p>
            <p>
              Anyone who knows the system passphrase can sign back in. Change the passphrase in the
              service configuration if a credential has leaked.
            </p>
          </>
        }
        onConfirm={() => void revokeAll()}
      />
    </>
  );
}
